import { c as _c } from "react/compiler-runtime";
import { Eye, FlaskConical, Search, Sparkles, ShoppingBasket, Heart } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

// --- Journey stage data ---

export interface JourneyStage {
  step: string;
  title: string;
  verb: string;
  description: string;
  icon: LucideIcon;
  accent: string;
  barWidth: string;
  barColor: string;
}
export const JOURNEY_STAGES: JourneyStage[] = [{
  step: '01',
  title: 'Awareness',
  verb: 'Get known',
  description: 'Reach the right people and become easy to notice and remember.',
  icon: Eye,
  accent: 'bg-[#ff5a36] text-white',
  barWidth: 'w-full',
  barColor: 'bg-[#ff5a36]'
}, {
  step: '02',
  title: 'Trial',
  verb: 'Get tried',
  description: 'Put the product in their hands and prove it delivers.',
  icon: FlaskConical,
  accent: 'bg-[#ff8a3d] text-gray-950',
  barWidth: 'w-[88%]',
  barColor: 'bg-[#ff8a3d]'
}, {
  step: '03',
  title: 'Consideration',
  verb: 'Build desire',
  description: 'Give people a reason to care, believe and choose you.',
  icon: Search,
  accent: 'bg-[#ffc857] text-gray-950',
  barWidth: 'w-[74%]',
  barColor: 'bg-[#ffc857]'
}, {
  step: '04',
  title: 'Intent',
  verb: 'Earn commitment',
  description: 'Move people from interest to decision—ready to act.',
  icon: Sparkles,
  accent: 'bg-[#8bd3a8] text-gray-950',
  barWidth: 'w-[60%]',
  barColor: 'bg-[#8bd3a8]'
}, {
  step: '05',
  title: 'Purchase',
  verb: 'Convert demand',
  description: 'Make your product available, obvious and easy to buy.',
  icon: ShoppingBasket,
  accent: 'bg-[#4fc3a1] text-gray-950',
  barWidth: 'w-[46%]',
  barColor: 'bg-[#4fc3a1]'
}, {
  step: '06',
  title: 'Loyalty',
  verb: 'Earn the next buy',
  description: 'Deliver value people come back for and recommend to others.',
  icon: Heart,
  accent: 'bg-[#159a84] text-white',
  barWidth: 'w-[32%]',
  barColor: 'bg-[#159a84]'
}];

// --- Variants ---

type Variant = 'dark' | 'light';
interface PurchaseJourneyFunnelProps {
  /** Visual variant — 'dark' for dark backgrounds, 'light' for white/cream backgrounds. */
  variant?: Variant;
  /** Title displayed above the funnel. Defaults to 'The Consumer Journey'. Pass null to hide. */
  title?: string | null;
  /** Whether to show the category header (Acquisition / Retention tabs). Defaults to true. */
  showHeader?: boolean;
  /** Whether to show the funnel bars panel. Defaults to true. */
  showBars?: boolean;
  /** Whether to show the stage descriptions grid. Defaults to true. */
  showStages?: boolean;
  /** Whether to show the footnote below the bars. Defaults to true. */
  showFootnote?: boolean;
  /** Additional className on the outer wrapper. */
  className?: string;
}
const variantStyles: Record<Variant, {
  wrapper: string;
  headerBorder: string;
  headerBg: string;
  headerText: string;
  retentionBg: string;
  barText: string;
  footnote: string;
  stageBorder: string;
  stageLabel: string;
  stageVerb: string;
  stageDescription: string;
  gridBorder: string;
}> = {
  dark: {
    wrapper: 'border-white/10 bg-white/[0.04] shadow-2xl shadow-black/30 backdrop-blur-sm',
    headerBorder: 'border-white/10',
    headerBg: '',
    headerText: 'text-white/80',
    retentionBg: 'bg-[#126f62]/40',
    barText: 'text-gray-950',
    footnote: 'text-gray-500',
    stageBorder: 'border-white/[0.06]',
    stageLabel: 'text-gray-500',
    stageVerb: 'text-white',
    stageDescription: 'text-gray-400',
    gridBorder: 'border-white/10'
  },
  light: {
    wrapper: 'border-2 border-gray-950 bg-white shadow-[8px_8px_0_0_#211d4f] sm:shadow-[12px_12px_0_0_#211d4f]',
    headerBorder: 'border-gray-950',
    headerBg: 'bg-[#211d4f] text-white',
    headerText: 'text-white',
    retentionBg: 'bg-[#126f62]',
    barText: 'text-gray-950',
    footnote: 'text-gray-500',
    stageBorder: 'border-gray-200',
    stageLabel: 'text-gray-400',
    stageVerb: 'text-gray-950',
    stageDescription: 'text-gray-600',
    gridBorder: 'border-gray-950'
  }
};

/**
 * Reusable purchase journey funnel visualisation.
 * Shows narrowing bars on the left and stage descriptions on the right.
 */
export function PurchaseJourneyFunnel(t0) {
  const $ = _c(39);
  const {
    variant: t1,
    title: t2,
    showHeader: t3,
    showBars: t4,
    showStages: t5,
    showFootnote: t6,
    className
  } = t0;
  const variant = t1 === undefined ? "dark" : t1;
  const title = t2 === undefined ? "The Consumer Journey" : t2;
  const showHeader = t3 === undefined ? true : t3;
  const showBars = t4 === undefined ? true : t4;
  const showStages = t5 === undefined ? true : t5;
  const showFootnote = t6 === undefined ? true : t6;
  const styles = variantStyles[variant];
  const hasBothPanels = showBars && showStages;
  let t7;
  if ($[0] !== className || $[1] !== styles.wrapper) {
    t7 = cn("overflow-hidden rounded-2xl", styles.wrapper, className);
    $[0] = className;
    $[1] = styles.wrapper;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  let t8;
  if ($[3] !== showHeader || $[4] !== styles.headerBorder || $[5] !== title || $[6] !== variant) {
    t8 = title && <div className={cn("px-5 py-4 text-center sm:px-7", showHeader && cn("border-b", styles.headerBorder))}><h3 className={cn("text-2xl font-black tracking-tight sm:text-2xl", variant === "dark" ? "text-white" : "text-gray-950")}>{title}</h3></div>;
    $[3] = showHeader;
    $[4] = styles.headerBorder;
    $[5] = title;
    $[6] = variant;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== showHeader || $[9] !== styles.headerBg || $[10] !== styles.headerBorder || $[11] !== styles.headerText || $[12] !== styles.retentionBg || $[13] !== variant) {
    t9 = showHeader && <div className={cn("grid border-b md:grid-cols-3", styles.headerBorder, variant === "light" && styles.headerBg)}><div className={cn("px-5 py-3 text-xs font-black uppercase tracking-[0.14em] md:col-span-2", styles.headerText)}>Acquisition · Create + Convert</div><div className={cn("border-t px-5 py-3 text-xs font-black uppercase tracking-[0.14em] md:border-l md:border-t-0", styles.headerBorder, styles.retentionBg, styles.headerText)}>Retention · Compound</div></div>;
    $[8] = showHeader;
    $[9] = styles.headerBg;
    $[10] = styles.headerBorder;
    $[11] = styles.headerText;
    $[12] = styles.retentionBg;
    $[13] = variant;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = hasBothPanels && "md:grid-cols-2";
  let t11;
  if ($[15] !== t10) {
    t11 = cn("grid", t10);
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== showBars || $[18] !== showFootnote || $[19] !== styles.barText || $[20] !== styles.footnote) {
    t12 = showBars && <div className="space-y-2.5 p-5 sm:p-7 lg:p-8">{JOURNEY_STAGES.map(stage => <div key={stage.title} className={cn("flex h-10 items-center justify-between rounded-sm px-4 font-black", styles.barText, stage.barWidth, stage.barColor)}><span className="text-sm">{stage.title}</span><span className="text-xs opacity-60">{stage.step}</span></div>)}{showFootnote && <p className={cn("pt-2 text-xs font-bold", styles.footnote)}>People narrow. Value should deepen.</p>}</div>;
    $[17] = showBars;
    $[18] = showFootnote;
    $[19] = styles.barText;
    $[20] = styles.footnote;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== showBars || $[23] !== showStages || $[24] !== styles.gridBorder || $[25] !== styles.stageBorder || $[26] !== styles.stageDescription || $[27] !== styles.stageLabel || $[28] !== styles.stageVerb) {
    t13 = showStages && <div className={cn("grid grid-cols-2", showBars && cn("border-t md:border-l md:border-t-0", styles.gridBorder))}>{JOURNEY_STAGES.map(stage_0 => {
        const Icon = stage_0.icon;
        return <article key={stage_0.step} className={cn("border-b border-r p-4 last:border-b-0 sm:p-5", styles.stageBorder)}><div className={cn("flex h-8 w-8 items-center justify-center rounded-full", stage_0.accent)}><Icon className="h-3.5 w-3.5" aria-hidden="true" /></div><p className={cn("mt-3 text-[10px] font-black uppercase tracking-[0.12em]", styles.stageLabel)}>{stage_0.title}</p><h3 className={cn("mt-0.5 text-sm font-black tracking-tight", styles.stageVerb)}>{stage_0.verb}</h3><p className={cn("mt-1.5 text-xs leading-[1.5]", styles.stageDescription)}>{stage_0.description}</p></article>;
      })}</div>;
    $[22] = showBars;
    $[23] = showStages;
    $[24] = styles.gridBorder;
    $[25] = styles.stageBorder;
    $[26] = styles.stageDescription;
    $[27] = styles.stageLabel;
    $[28] = styles.stageVerb;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t11 || $[31] !== t12 || $[32] !== t13) {
    t14 = <div className={t11}>{t12}{t13}</div>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t14 || $[35] !== t7 || $[36] !== t8 || $[37] !== t9) {
    t15 = <div className={t7}>{t8}{t9}{t14}</div>;
    $[34] = t14;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  return t15;
}